import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Modal from "../../../../../components/Model_Power";
import ToggleTabs from "../../utility/ChartTableToggle";
import {
    getSummaryData,
    SummaryDataResponse,
    getEntityDelta,
    EntityDeltaResponse,
    getIntervalSummary,
    IntervalSummaryResponse
} from "../../../../../Rest_api/RestApi";

import { SearchBox } from "../../../../../components/Search";
import { X } from "lucide-react";

// Shared Components
import SummaryTilesCurrentValues from "./Tiles/SummaryTilesCurrentValues";
import SummaryTilesChangedValues from "./Tiles/SummaryTilesChangedValues";
import SummaryTableInjection from "./Tables/SummaryTableInjection";
import SummaryTableDrawal from "./Tables/SummaryTableDrawal";

interface SummaryPopupProps {
    isOpen: boolean;
    onClose: () => void;
    selectedExchanges?: string[];
    selectedMarkets?: string[];
}

const TimeBlockTabs = ["Full Day", "Time Block"];
const EntityTabs = ["Injection", "Drawal"];

const SummaryPopup: React.FC<SummaryPopupProps> = ({ isOpen, onClose, selectedExchanges, selectedMarkets }) => {
    const [activeTimeTab, setActiveTimeTab] = useState<string>("Full Day");
    const [activeEntityTab, setActiveEntityTab] = useState<string>("Injection");

    // Integration Logic
    const DefaultDates = useSelector(
        (state: any) => state.defaultDatesSetter.defaultDates
    );
    const [summaryData, setSummaryData] = useState<SummaryDataResponse | null>(null);
    const [entityDeltaData, setEntityDeltaData] = useState<EntityDeltaResponse | null>(null);
    const [intervalSummaryData, setIntervalSummaryData] = useState<IntervalSummaryResponse | null>(null);
    const [availableDates, setAvailableDates] = useState<any[]>([]);
    const [selectedComparisonDates, setSelectedComparisonDates] = useState<any[]>([]); // New state for selected dates
    const [loading, setLoading] = useState(false);

    // Sync available dates from Redux
    useEffect(() => {
        const dateKey = "Power Market";
        const trendDates = DefaultDates?.[dateKey]?.defaultTrendDates || [];
        setAvailableDates(trendDates);

        // Initialize selected dates with first 2 available dates
        if (trendDates.length >= 2) {
            setSelectedComparisonDates([trendDates[0], trendDates[1]]);
        } else if (trendDates.length > 0) {
            setSelectedComparisonDates(trendDates);
        } else {
            setSelectedComparisonDates([]);
        }
    }, [DefaultDates]);

    useEffect(() => {
        const fetchData = async () => {
            // Need at least 2 date ranges for comparison data
            if (selectedComparisonDates.length < 2) {
                setSummaryData(null);
                setEntityDeltaData(null);
                setIntervalSummaryData(null);
                return;
            }

            setLoading(true);
            try {
                // Take the first two selected date ranges
                const range1 = selectedComparisonDates[0].value.split(",");
                const range2 = selectedComparisonDates[1].value.split(",");

                const startDate1 = range1[0];
                const endDate1 = range1[1] || startDate1;
                const startDate2 = range2[0];
                const endDate2 = range2[1] || startDate2;

                if (activeTimeTab === "Full Day") {
                    // Fetch Summary Data (Tiles)
                    try {
                        const summaryRes = await getSummaryData(
                            startDate1,
                            endDate1,
                            startDate2,
                            endDate2,
                            selectedExchanges,
                            selectedMarkets
                        );
                        setSummaryData(summaryRes);
                    } catch (error) {
                        console.error("Error fetching summary data:", error);
                        setSummaryData(null);
                    }

                    // Fetch Entity Delta Data (Tables)
                    try {
                        const entityDeltaRes = await getEntityDelta(
                            startDate1,
                            endDate1,
                            startDate2,
                            endDate2,
                            selectedExchanges,
                            selectedMarkets
                        );
                        setEntityDeltaData(entityDeltaRes);
                    } catch (error) {
                        console.error("Error fetching entity delta data:", error);
                        setEntityDeltaData(null);
                    }
                } else if (activeTimeTab === "Time Block") {
                    // Fetch Interval Summary Data (Everything)
                    try {
                        const intervalRes = await getIntervalSummary(
                            startDate1,
                            endDate1,
                            startDate2,
                            endDate2,
                            selectedExchanges,
                            selectedMarkets
                        );
                        setIntervalSummaryData(intervalRes);
                    } catch (error) {
                        console.error("Error fetching interval summary data:", error);
                        setIntervalSummaryData(null);
                    }
                }
            } catch (err) {
                console.error("General error in fetchData", err);
            } finally {
                setLoading(false);
            }
        };

        if (isOpen) {
            fetchData();
        }
    }, [selectedComparisonDates, isOpen, activeTimeTab, selectedExchanges, selectedMarkets]);

    // Derived data for display based on active tab
    const displayCurrentValues = activeTimeTab === "Full Day" ? summaryData?.current_values : intervalSummaryData?.current_values;
    const displayChangedValues = activeTimeTab === "Full Day" ? summaryData?.changed_values : intervalSummaryData?.changed_values;
    const displayCurrentDate = activeTimeTab === "Full Day" ? summaryData?.current_date : intervalSummaryData?.current_date;
    const displayComparisonDate = activeTimeTab === "Full Day" ? summaryData?.comparison_date : intervalSummaryData?.comparison_date;

    const displayInjectionData = activeTimeTab === "Full Day" ? entityDeltaData?.top_injection_changes : intervalSummaryData?.top_injection_changes;
    const displayDrawalData = activeTimeTab === "Full Day" ? entityDeltaData?.top_drawal_changes : intervalSummaryData?.top_drawal_changes;

    // Time block info for header
    const timeBlockInfo = activeTimeTab === "Time Block" && intervalSummaryData?.timeblocks
        ? `(${intervalSummaryData.interval})`
        : "(14:00 to 18:00 hrs)"; // Fallback or dynamic logic if provided

    // Helper to format date range string
    const formatDateRange = (range?: { start: string, end: string }) => {
        if (!range || !range.start) return "";
        if (range.start === range.end || !range.end) return range.start;
        return `${range.start} to ${range.end}`;
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            width="1500px"
            maxWidth="90vw"
            height="auto"
            maxHeight="90vh"
            showCloseButton={false} // Hide default close button
            title="" // Remove default title
        >
            {/* Custom Header to place SearchBox next to Close Button */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#DFEAF2] bg-white -mx-4 -mt-4 mb-4 sticky top-0 z-50">
                <h5 className="text-base text-[#525062] font-semibold">Summary</h5>
                <div className="flex items-center gap-4">
                    <div className="w-[250px] [&_div]:!text-xs [&>div>div]:!min-h-[32px] [&>div>div]:!py-0">
                        <SearchBox
                            options={availableDates}
                            isMany={true}
                            placeholder="Select Dates"
                            selectedOptions={selectedComparisonDates}
                            onChange={(val: any) => {
                                if (val && val.length > 0) {
                                    setSelectedComparisonDates(Array.isArray(val) ? val : [val]);
                                } else {
                                    setSelectedComparisonDates([]);
                                }
                            }}
                        />
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                        <X size={20} />
                    </button>
                </div>
            </div>

            <div className="space-y-4 w-full">
                <div className="rounded-xl w-full border border-[#DFEAF2] overflow-hidden">
                    {/* Time Block Header - White background */}
                    <div className="flex items-center justify-between px-4 py-2 bg-white">
                        <div className="text-sm text-[#525062] font-medium">
                            {activeTimeTab === "Full Day" ? (
                                <>
                                    <span className="font-semibold">Full Day</span>
                                    <span className="text-[#8C8A96] ml-1 text-base">(Blocks: 1-96)</span>
                                </>
                            ) : (
                                <>
                                    <span className="font-semibold">TIME with MAX MCP Change</span>
                                    {/* Display dynamic interval info if available, else static placeholder */}
                                    <span className="text-[#8C8A96] ml-1 text-base">
                                        {intervalSummaryData?.interval ? (
                                            <>
                                                ({intervalSummaryData.interval})
                                                {intervalSummaryData.timeblocks && intervalSummaryData.timeblocks.length > 0 && (
                                                    <span className="ml-1">
                                                        (Blocks: {intervalSummaryData.timeblocks.join("-")})
                                                    </span>
                                                )}
                                            </>
                                        ) : ""}
                                    </span>
                                </>
                            )}

                            {/* Comparison Dates Display */}
                            {(displayCurrentDate?.start || displayComparisonDate?.start) && (
                                <span className="ml-4 text-xs font-normal text-[#525062] bg-[#F7F9FC] px-2 py-1 rounded border border-[#DFEAF2]">
                                    <span className="font-medium text-[#F1935C]">Current Date Range: </span>
                                    {formatDateRange(displayCurrentDate)}
                                    <span className="mx-2 text-gray-300">|</span>
                                    <span className="font-medium text-[#3E97A9]">Previous Date Range: </span>
                                    {formatDateRange(displayComparisonDate)}
                                </span>
                            )}

                        </div>
                        <ToggleTabs
                            tabs={TimeBlockTabs}
                            activeTab={activeTimeTab}
                            setActiveTab={setActiveTimeTab}
                        />
                    </div>

                    {/* Market Summary Text */}
                    {(activeTimeTab === "Full Day" ? summaryData?.market_summary : intervalSummaryData?.market_summary) && (
                        <div className="px-4 py-3 bg-[#FFF8F1] border-b border-[#FBE0C8]">
                            <div className="flex gap-2">
                                <span className="text-lg">💡</span>
                                <p className="text-sm text-[#7A4B26] leading-relaxed font-medium">
                                    {activeTimeTab === "Full Day" ? summaryData?.market_summary : intervalSummaryData?.market_summary}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Tiles Section - Gray background */}
                    <div className="bg-[#F8FAFC] p-2">
                        <div className="space-y-3">
                            {availableDates.length < 2 ? (
                                <div className="flex items-center justify-center p-4">
                                    <p className="text-gray-500 text-base">Please select 2 date ranges to view Summary comparison.</p>
                                </div>
                            ) : (
                                <>
                                    {/* Current Values Section */}
                                    <SummaryTilesCurrentValues
                                        data={displayCurrentValues}
                                    />

                                    {/* Changed Values Section */}
                                    <SummaryTilesChangedValues
                                        data={displayChangedValues}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Card 2: Entities Table Section */}
                <div className="rounded-xl w-full border border-[#DFEAF2] overflow-hidden">
                    {/* Table Header - White background */}
                    <div className="flex items-center justify-between px-4 py-2 bg-white">
                        <h3 className="text-sm font-semibold text-[#525062]">
                            Entities with Maximum Change in {activeEntityTab}
                        </h3>
                        <ToggleTabs
                            tabs={EntityTabs}
                            activeTab={activeEntityTab}
                            setActiveTab={setActiveEntityTab}
                        />
                    </div>

                    {/* Table Content - No wrapper, direct table */}
                    <div className="px-2 pb-2">
                        {availableDates.length < 2 ? (
                            <div className="flex items-center justify-center p-4">
                                <p className="text-gray-500 text-sm">Please select 2 date ranges to view Summary comparison.</p>
                            </div>
                        ) : (
                            <>
                                {activeEntityTab === "Injection" && (
                                    <SummaryTableInjection
                                        data={displayInjectionData}
                                        hideOutage={activeTimeTab === "Time Block"}
                                        loading={loading}
                                    />
                                )}
                                {activeEntityTab === "Drawal" && (
                                    <SummaryTableDrawal
                                        data={displayDrawalData}
                                        hideOutage={activeTimeTab === "Time Block"}
                                        loading={loading}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Modal >
    );
};

export default SummaryPopup;
